/**
 * Token Manager - Secure token storage and management
 * 
 * SECURITY CONTEXT:
 * - Tokens stored in localStorage are vulnerable to XSS attacks
 * - This module provides two modes:
 *   1. httpOnly cookies (Sanctum stateful) - RECOMMENDED, requires backend config
 *   2. localStorage with refresh rotation - Fallback with reduced TTL
 * 
 * Reference: Security Audit #15 - Access token in LocalStorage
 */

const USE_HTTPONLY_COOKIES = process.env.VUE_APP_USE_HTTPONLY_COOKIES === 'true';
const TOKEN_TTL_MINUTES = parseInt(process.env.VUE_APP_TOKEN_TTL_MINUTES || '60', 10);

// Track token metadata for anomaly detection
const tokenMetadata = {
  issuedAt: null,
  expiresAt: null,
  lastUsedAt: null,
  refreshCount: 0,
  fingerprint: null, // User agent + IP hash for anomaly detection
};

/**
 * Get current token fingerprint (basic anomaly detection)
 * In production, combine with backend IP validation
 */
function getTokenFingerprint() {
  const userAgent = navigator.userAgent;
  // Simple hash of user agent - in production, combine with server-side IP validation
  return btoa(userAgent).substring(0, 20);
}

/**
 * Check if token is nearing expiration (anomaly detection trigger)
 */
function isTokenNearExpiry() {
  if (!tokenMetadata.expiresAt) return false;
  const now = Date.now();
  const timeUntilExpiry = tokenMetadata.expiresAt - now;
  
  return timeUntilExpiry <= 0;
}

/**
 * Store token with metadata (localStorage fallback mode)
 */
function storeToken(token) {
  if (USE_HTTPONLY_COOKIES) {
    // With httpOnly cookies, frontend doesn't store the token
    return;
  }

  const now = Date.now();
  const expiresAt = now + (TOKEN_TTL_MINUTES * 60 * 1000);
  
  tokenMetadata.issuedAt = now;
  tokenMetadata.expiresAt = expiresAt;
  tokenMetadata.lastUsedAt = now;
  tokenMetadata.fingerprint = getTokenFingerprint();
  tokenMetadata.refreshCount = 0;
  
  try {
    localStorage.setItem('access_token', token);
    localStorage.setItem('token_issued_at', now.toString());
    localStorage.setItem('token_expires_at', expiresAt.toString());

  } catch (error) {
    console.error('[Security] Failed to store token:', error);
  }
}

/**
 * Retrieve current token
 */
function getToken() {
  if (USE_HTTPONLY_COOKIES) {
    // With httpOnly cookies, token is automatically sent with requests
    return null;
  }
  
  const token = localStorage.getItem('access_token');
  
  if (token) {
    tokenMetadata.lastUsedAt = Date.now();
    
    // Check expiration
    if (isTokenNearExpiry()) {
      clearToken();
      return null;
    }
  }
  
  return token;
}

/**
 * Clear token and metadata
 */
function clearToken() {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token_issued_at');
    localStorage.removeItem('token_expires_at');
    
    tokenMetadata.issuedAt = null;
    tokenMetadata.expiresAt = null;
    tokenMetadata.lastUsedAt = null;
    tokenMetadata.refreshCount = 0;
    tokenMetadata.fingerprint = null;
  } catch (error) {
    console.error('[Security] Failed to clear token:', error);
  }
}

/**
 * Check if using httpOnly cookies (stateful authentication)
 */
function isUsingHttpOnlyCookies() {
  return USE_HTTPONLY_COOKIES;
}

/**
 * Log anomaly - used for detecting suspicious token usage patterns
 */
function logAnomaly(reason, details = {}) {
  void reason;
  void details;
  // In production: send to security logging backend
  // Example: POST /api/security/anomalies
  // with reason and details for server-side analysis
}

/**
 * Increment refresh count and track refresh pattern
 */
function recordTokenRefresh() {
  if (USE_HTTPONLY_COOKIES) return;
  
  tokenMetadata.refreshCount++;
  
  // Anomaly detection: too many refreshes in short time = possible attack
  if (tokenMetadata.refreshCount > 100) {
    logAnomaly('Excessive token refreshes detected', {
      refreshCount: tokenMetadata.refreshCount,
    });
  }
}

export default {
  storeToken,
  getToken,
  clearToken,
  isUsingHttpOnlyCookies,
  isTokenNearExpiry,
  logAnomaly,
  recordTokenRefresh,
  getTokenMetadata: () => ({ ...tokenMetadata }),
};
